import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";

import { AliasEditorComponent } from "../../../../components/common/alias-editor/alias-editor.component";
import { TrafficLineChart } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.component";
import { TrafficSubject } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.helpers";
import { EmptyState } from "../../../../components/subscription-detail/empty-state/empty-state";
import { FwDetailContainer } from "../../../../components/subscription-detail/fw-detail-container/fw-detail-container";
import { Networkgraph } from "../../../../components/subscription-detail/networkgraph/networkgraph";
import { PageTabComponent } from "../../../../components/subscription-detail/page-tab/page-tab";
import { ServiceId } from "../../../../components/subscription-detail/service-id/service-id";
import { SubscriptionDetailHeader } from "../../../../components/subscription-detail/subscription-detail-header/subscription-detail-header";
import { ThirdPartyServiceIdsRowContentComponent } from "../../../../components/subscription-detail/third-party-service-ids-row-content/third-party-service-ids-row-content";
import { ServiceSpeedPipe } from "../../../../helpers/service-speed-pipe/service-speed-pipe";
import { SubscriptionDatePipe } from "../../../../helpers/subscription-datepipe/subscription-datepipe";
import { toInteger } from "../../../../helpers/to-integer";
import { FirewallSubscriptionDetails } from "../../types";
import { SubscriptionViewBase } from "../subscription-view-base";
import { FirewallShortcutMenu } from "./components/firewall-shortcut-menu/firewall-shortcut-menu";
import { UplinkDetailContainer } from "./components/uplink-detail-container/uplink-detail-container";

@Component({
  selector: "firewall-subscription-view",
  templateUrl: "firewall-subscription-view.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AliasEditorComponent,
    AngularSvgIconModule,
    EmptyState,
    FirewallShortcutMenu,
    Networkgraph,
    PageTabComponent,
    ServiceId,
    ServiceSpeedPipe,
    SubscriptionDatePipe,
    SubscriptionDetailHeader,
    FwDetailContainer,
    ThirdPartyServiceIdsRowContentComponent,
    TrafficLineChart,
    TranslatePipe,
    UplinkDetailContainer,
  ],
})
export class FirewallSubscriptionViewComponent extends SubscriptionViewBase<FirewallSubscriptionDetails> {
  protected availabilityZone = computed(() => this.subscription().availabilityZone);
  protected domain = computed(() => this.subscription().domain);
  protected fwAsn = computed(() => toInteger(this.subscription().fwAsn));
  protected serviceSpeed = computed(() => this.subscription().serviceSpeed);
  protected topology = computed(() => this.subscription().topology);
  protected ipGateway = computed(() => this.subscription().ipGateway);

  private translate = inject(TranslateService);
  protected chartSubjects = computed<TrafficSubject[]>(() =>
    buildFirewallChartSubjects(this.subscription(), this.translate),
  );
}

export function buildFirewallChartSubjects(
  subscription: FirewallSubscriptionDetails,
  translate: TranslateService,
): TrafficSubject[] {
  const uplinkTitle = translate.instant("ServiceElement.SURFinternetUplink");

  const uplinkItem: TrafficSubject = {
    subscriptionId: subscription.subscriptionId,
    subscriptionInstanceIds: subscription.ipGateway?.sapSubscriptionInstanceIds,
    name: uplinkTitle,
  };

  const l2Items: TrafficSubject[] = subscription.l2Endpoints.map((endpoint) => ({
    subscriptionId: endpoint.subscriptionId,
    subscriptionInstanceIds: endpoint.sapSubscriptionInstanceIds,
    name: endpoint.description,
  }));

  const l3Items: TrafficSubject[] = subscription.l3Endpoints.map((endpoint) => ({
    subscriptionId: endpoint.subscriptionId,
    subscriptionInstanceIds: endpoint.sapSubscriptionInstanceIds,
    name: endpoint.description,
  }));

  return [uplinkItem, ...l2Items, ...l3Items];
}
